import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Mail, User, Clock, CheckCircle, Eye, Trash2, Download, Printer, FileJson } from 'lucide-react'
import { db } from '../../firebase'
import { collection, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore'
import ConfirmModal from '../ConfirmModal'
import { exportAsJSON, exportAsCSV, exportAsText, printItem } from '../../utils/helpers.jsx'
import QuickNotes from './QuickNotes'

const ContactSection = () => {
  const [contacts, setContacts] = useState([])
  const [selectedContact, setSelectedContact] = useState(null)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, contactId: null })

  useEffect(() => {
    const contactsRef = collection(db, 'contactSubmissions')
    
    const unsubscribe = onSnapshot(contactsRef, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }))
      loaded.sort((a, b) => b.createdAt - a.createdAt)
      setContacts(loaded)
    })

    return () => unsubscribe()
  }, [])

  const handleMarkAsRead = async (id) => {
    try {
      const contactRef = doc(db, 'contactSubmissions', id)
      await updateDoc(contactRef, { read: true })
    } catch (error) {
      console.error('Error marking as read:', error)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'contactSubmissions', id))
      if (selectedContact?.id === id) {
        setSelectedContact(null)
      }
    } catch (error) {
      console.error('Error deleting contact:', error)
    }
  }

  const openDeleteConfirm = (id) => {
    setConfirmModal({ isOpen: true, contactId: id })
  }

  const closeDeleteConfirm = () => {
    setConfirmModal({ isOpen: false, contactId: null })
  }

  const confirmDelete = () => {
    if (confirmModal.contactId) {
      handleDelete(confirmModal.contactId)
    }
  }

  return (
    <div className="h-[calc(100vh-200px)] flex gap-6">
      {/* Contacts List */}
      <div className="w-96 bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-4 overflow-y-auto">
        <h3 className="text-lg font-display font-bold text-gold-400 mb-4 flex items-center">
          <Mail className="w-5 h-5 mr-2" />
          Forma e Kontaktit ({contacts.length})
        </h3>

        <div className="space-y-2">
          {contacts.map((contact) => (
            <motion.div
              key={contact.id}
              onClick={() => setSelectedContact(contact)}
              className={`p-4 rounded-xl cursor-pointer transition-all ${
                selectedContact?.id === contact.id
                  ? 'bg-gradient-gold text-white'
                  : contact.read
                  ? 'bg-dark/20 hover:bg-dark/40 text-beige-300'
                  : 'bg-dark/40 hover:bg-dark/60 text-cream border-l-4 border-gold-500'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    {contact.name}
                  </p>
                  <p className="text-xs opacity-80 mt-1">{contact.email}</p>
                </div>
                {!contact.read && (
                  <div className="w-2 h-2 bg-gold-400 rounded-full animate-pulse"></div>
                )}
              </div>
              <p className="text-xs opacity-70 truncate">{contact.message}</p>
              <p className="text-xs opacity-50 mt-2">
                {contact.createdAt.toLocaleDateString('sq-AL')} {contact.createdAt.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact Detail */}
      <div className="flex-1 bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-6">
        {selectedContact ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-display font-bold text-gold-400">
                  {selectedContact.name}
                </h3>
                <p className="text-beige-300">{selectedContact.email}</p>
              </div>
              <div className="flex space-x-2">
                {!selectedContact.read && (
                  <button
                    onClick={() => handleMarkAsRead(selectedContact.id)}
                    className="btn-secondary text-sm"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Shëno si të lexuar
                  </button>
                )}
                
                {/* Export Dropdown */}
                <div className="relative group">
                  <button
                    className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-xl transition-all text-sm"
                    title="Export contact"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <div className="absolute right-0 mt-2 w-40 bg-bronze-900 border border-gold-500/30 rounded-lg shadow-gold opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                    <button
                      onClick={() => exportAsJSON(selectedContact, `contact-${selectedContact.name.replace(/\s/g, '-').toLowerCase()}`)}
                      className="w-full px-4 py-2 text-left text-sm text-beige-200 hover:bg-gold-500/20 flex items-center rounded-t-lg"
                    >
                      <FileJson className="w-3 h-3 mr-2" />
                      Export JSON
                    </button>
                    <button
                      onClick={() => exportAsCSV(selectedContact, `contact-${selectedContact.name.replace(/\s/g, '-').toLowerCase()}`)}
                      className="w-full px-4 py-2 text-left text-sm text-beige-200 hover:bg-gold-500/20 flex items-center"
                    >
                      <Download className="w-3 h-3 mr-2" />
                      Export CSV
                    </button>
                    <button
                      onClick={() => exportAsText(selectedContact, `contact-${selectedContact.name.replace(/\s/g, '-').toLowerCase()}`, `Contact - ${selectedContact.name}`)}
                      className="w-full px-4 py-2 text-left text-sm text-beige-200 hover:bg-gold-500/20 flex items-center"
                    >
                      <Download className="w-3 h-3 mr-2" />
                      Export TXT
                    </button>
                    <button
                      onClick={() => printItem(selectedContact, `Contact - ${selectedContact.name}`)}
                      className="w-full px-4 py-2 text-left text-sm text-beige-200 hover:bg-gold-500/20 flex items-center rounded-b-lg"
                    >
                      <Printer className="w-3 h-3 mr-2" />
                      Print
                    </button>
                  </div>
                </div>
                
                <button
                  onClick={() => openDeleteConfirm(selectedContact.id)}
                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-all text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="bg-dark/30 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-beige-300 mb-2">Mesazhi:</h4>
              <p className="text-cream leading-relaxed">{selectedContact.message}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-dark/30 rounded-xl p-4">
                <p className="text-sm text-beige-400 mb-1">Data e dërgimit</p>
                <p className="text-cream font-semibold">
                  {selectedContact.createdAt.toLocaleDateString('sq-AL')}
                </p>
              </div>
              <div className="bg-dark/30 rounded-xl p-4">
                <p className="text-sm text-beige-400 mb-1">Ora</p>
                <p className="text-cream font-semibold">
                  {selectedContact.createdAt.toLocaleTimeString('sq-AL', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <a
                href={`mailto:${selectedContact.email}`}
                className="btn-primary flex-1 justify-center"
              >
                <Mail className="w-4 h-4 mr-2" />
                Përgjigju me Email
              </a>
              <a
                href={`https://wa.me/${selectedContact.phone || ''}`}
                className="btn-secondary flex-1 justify-center"
                target="_blank"
                rel="noopener noreferrer"
              >
                Kontakto në WhatsApp
              </a>
            </div>

            {/* Quick Notes Section */}
            <div className="mt-6 pt-6 border-t border-gold-500/20">
              <QuickNotes 
                clientId={selectedContact.id} 
                clientName={selectedContact.name}
                clientType="contact"
              />
            </div>
          </motion.div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Mail className="w-16 h-16 text-beige-400 mx-auto mb-4 opacity-50" />
              <p className="text-beige-300">Zgjidh një kontakt për të parë detajet</p>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
        title="Fshi Kontaktin"
        message="Dëshiron ta fshish këtë kontakt? Kë veprim nuk mund të zhbëhet."
        confirmText="Po, Fshije"
        cancelText="Jo, Anulo"
      />
    </div>
  )
}

export default ContactSection
